import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { tokens } from "../theme";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { generateQRCode } from "../utils/qrcodeGen";
import { ViewDocumentModal } from "./modals/ViewDocumentModal";

const ManageMarketer = ({ marketer, onBack }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();
  const { snackError } = useAuth();

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDocModal, setViewDocModal] = useState({ open: false, imgUrl: "" });

  const fetchAssignedUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get(
        `/card-service/cms/admin/marketers/assigned-card-users/${marketer.marketerUserEmail}`
      );
      if (response?.data?.status === true || response?.data) {
        const data = response?.data?.data || response?.data || [];
        setAssignedUsers(data);
      } else {
        snackError("Failed to fetch assigned users");
      }
    } catch (err) {
      console.error(err);
      snackError("An error occurred while fetching assigned users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignedUsers();
  }, [marketer.marketerUserEmail]);

  const columns = [
    { field: "name", headerName: "Full Name", width: 180 },
    { field: "email", headerName: "Email", width: 220 },
    {
      field: "phoneNumber",
      headerName: "Phone",
      width: 150,
      valueGetter: (params) => params.row.phoneNumber || "—",
    },
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 150,
      valueGetter: (params) => params.row.accountNumber || "—",
    },
    {
      field: "cardPan",
      headerName: "Card PAN",
      width: 180,
      valueGetter: (params) => params.row.cardPan || "—",
    },
    {
      field: "address",
      headerName: "Address",
      width: 250,
      renderCell: ({ row }) => {
        const addrParts = [row.street, row.town, row.address].filter(Boolean);
        return <Typography>{addrParts.length > 0 ? addrParts.join(", ") : "—"}</Typography>;
      },
    },
    {
      field: "dateAssigned",
      headerName: "Date Assigned",
      width: 180,
      renderCell: ({ row: { dateAssigned } }) => {
        return <Typography>{dateAssigned ? new Date(dateAssigned).toLocaleString() : "—"}</Typography>;
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      renderCell: ({ row: { qrCode } }) => {
        return (
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: colors.orangeAccent[700],
              color: colors.grey[100],
            }}
            onClick={async () => {
              if (!qrCode) {
                snackError("No QR Code associated with this assignment");
                return;
              }
              const imgUrl = await generateQRCode(qrCode);
              setViewDocModal({
                open: true,
                imgUrl,
              });
            }}
          >
            View QR Code
          </Button>
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <ViewDocumentModal modal={viewDocModal} setModal={setViewDocModal} />
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
        >
          Back to Marketers
        </Button>
        <Box textAlign="right">
          <Typography variant="h3" fontWeight="bold">
            Marketer: {marketer.fullName || marketer.marketerUserEmail}
          </Typography>
          <Typography variant="h6" color={colors.grey[300]}>
            {marketer.marketerUserEmail}
          </Typography>
        </Box>
      </Box>

      <Box
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.redAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.redAccent[700],
          },
        }}
      >
        <DataGrid
          loading={loading}
          rows={assignedUsers}
          columns={columns}
          getRowId={(row) => row.id || row.email}
        />
      </Box>
    </Box>
  );
};

export default ManageMarketer;
